import React from 'react';
import {useTranslation} from 'react-i18next';
import {DisplayedPayment} from '../types';
import {RESERVATION_PAYMENT_TYPES} from '@chekin/core';
import {MutateOptions} from '@tanstack/query-core';
import {PAYMENT_ITEMS_WITH_MORE_INFO_MODAL} from '../constants';
import {usePaymentItemModals} from './usePaymentItemModals';
import {Dropdown} from '../../common/Dropdown/Dropdown';
import {Button} from '@chekinapp/ui';
import QuestionMarkAndText from 'components/common/QuestionMarkAndText';
import {
  Container,
  ContentPrice,
  ContentTitle,
  DotsButton,
  Fee,
  Label,
  Price,
  SubContainerPrice,
  SubTitle,
  Title,
} from './styled';
import {Plus, SquarePen} from 'lucide-react';

type PaymentItemProps = {
  payment: DisplayedPayment;
  onRemove?: (
    externalId?: string,
    options?: MutateOptions<unknown, unknown, unknown>,
  ) => boolean | void;
  onPayLater?: (paymentId: string) => void;
  currency?: string;
  className?: string;
};

function PaymentItem({
  currency,
  onRemove,
  onPayLater,
  payment: {
    id,
    externalId,
    isRemovable,
    isEditable,
    title,
    type,
    onEdit,
    price,
    fee,
    label,
    objType,
    isPMSorigin,
  },
}: PaymentItemProps) {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);

  const {openModal, Modal} = usePaymentItemModals({
    paymentType: objType as RESERVATION_PAYMENT_TYPES,
  });

  const dropdownOptions = React.useMemo(
    () => [
      {
        id: 1,
        label: t('add_to_pay_later'),
        onClick: () => onPayLater?.(id),
      },
      {
        id: 2,
        label: t('remove_item'),
        onClick: () => {
          setIsLoading(true);
          const isRemoveStarted = onRemove?.(externalId, {
            onSettled: () => {
              setIsLoading(false);
            },
          });

          if (isRemoveStarted === false) {
            setIsLoading(false);
          }
        },
        hidden: !isRemovable,
      },
    ],
    [t, isRemovable, onPayLater, id, onRemove, externalId],
  );

  return (
    <Container $isLoading={isLoading} className="payment-item">
      <ContentTitle>
        <Title className="payment-item__item">
          <span>{title}</span>
          {objType && PAYMENT_ITEMS_WITH_MORE_INFO_MODAL.includes(objType) && (
            <QuestionMarkAndText onClick={openModal} />
          )}
        </Title>
        {label && <Label className="payment-item__label">({label})</Label>}
        {type && type !== 'ONLINE_PAYMENT' && (
          <SubTitle
            className="payment-item__subtitle"
            $blue={type === 'PRE_AUTH_PAYMENT'}
          >
            {t(`payment_types.${type.toLowerCase()}`)}
          </SubTitle>
        )}

        {isEditable && (
          <Button
            onClick={onEdit}
            variant="link"
            className="payment-item__edit-btn mt-2 h-6 gap-1 p-0 mr-auto"
          >
            {objType === RESERVATION_PAYMENT_TYPES.touristTaxes ? (
              <>
                <Plus size={24} />
                {t('add_exemptions')}
              </>
            ) : (
              <>
                <SquarePen />
                {t('edit_options')}
              </>
            )}
          </Button>
        )}
      </ContentTitle>
      <ContentPrice className="payment-item__price-content">
        <SubContainerPrice>
          <Price className="payment-item__price">
            {price}
            {currency}
          </Price>
          {!isPMSorigin && (
            <Dropdown
              position="bottom right"
              options={dropdownOptions}
              disabled={isLoading}
              trigger={
                <div>
                  <DotsButton name="dots" size={16} />
                </div>
              }
            />
          )}
        </SubContainerPrice>
        {fee && (
          <Fee>
            + {fee}
            {currency} {t('fee')}
          </Fee>
        )}
      </ContentPrice>
      {Modal && <Modal />}
    </Container>
  );
}

export {PaymentItem};
